<template>
  <div class="slider-area">
    <swiper
      :key="swiperRenderKey"
      :modules="swiperModules"
      :slides-per-view="1"
      :space-between="0"
      :loop="displaySliders.length > 1"
      :autoplay="heroAutoplay"
      :pagination="{ clickable: true }"
      class="swiper-container slider__active slider-wrapper"
      @swiper="onSwiper"
    >
      <swiper-slide
        v-for="slider in displaySliders"
        :key="slider.id"
        class="single-slider slider-height d-flex align-items-center"
        :style="slideBackgroundStyle(slider)"
      >
        <div class="container">
          <div class="row">
            <div class="col-xl-5">
              <div class="slider-content">
                <div class="slider-top-btn" data-animation="fadeInLeft" data-delay="1.5s">
                  <router-link to="/shop" class="st-btn b-radius">{{ slider.tag }}</router-link>
                </div>
                <h2
                  data-animation="fadeInLeft"
                  data-delay="1.7s"
                  class="pt-15 slider-title pb-5"
                  v-html="slider.title"
                />
                <p class="pr-20 slider_text" data-animation="fadeInLeft" data-delay="1.9s">
                  {{ slider.text }}
                </p>
                <div class="slider-bottom-btn mt-75">
                  <a
                    v-if="ctaIsExternal(slider)"
                    :href="slider.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-animation="fadeInUp"
                    data-delay="1.15s"
                    class="st-btn-b b-radius"
                  >
                    Discover now
                  </a>
                  <router-link
                    v-else
                    :to="ctaPath(slider)"
                    data-animation="fadeInUp"
                    data-delay="1.15s"
                    class="st-btn-b b-radius"
                  >
                    Discover now
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </swiper-slide>
    </swiper>
  </div>
</template>

<script>
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Autoplay, Pagination } from 'swiper/modules';
import axiosInstance from '@/services/axios';
import 'swiper/css';
import 'swiper/css/pagination';

const FALLBACK_SLIDERS = [
  {
    id: 'fallback-1',
    tag: 'New collection',
    title: 'Welcome to our store',
    text: 'Add sliders in the admin panel or check your API connection.',
    image_url: '',
    _gradient: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 45%, #0f3460 100%)',
  },
  {
    id: 'fallback-2',
    tag: 'Shop now',
    title: 'Best deals online',
    text: 'Browse products and enjoy fast delivery.',
    image_url: '',
    _gradient: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
  },
];

function normalizeSliderPayload(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.data)) return data.data;
  if (data && Array.isArray(data.sliders)) return data.sliders;
  return [];
}

/** Laravel often returns /storage/... ; Vue dev server must load files from backend origin. */
function backendOrigin() {
  const site = (process.env.VUE_APP_SITE_URL || '').trim().replace(/\/$/, '');
  if (site) return site;
  const fromAxios = (axiosInstance.defaults.baseURL || '').trim().replace(/\/$/, '');
  if (fromAxios.endsWith('/api')) return fromAxios.slice(0, -4);
  if (fromAxios) return fromAxios;
  const api = (process.env.VUE_APP_API_URL || '').trim().replace(/\/$/, '');
  if (api.endsWith('/api')) return api.slice(0, -4);
  return api;
}

function resolveSliderImageUrl(href) {
  if (!href || typeof href !== 'string') return '';
  const u = href.trim();
  if (/^https?:\/\//i.test(u)) return u;
  const origin = backendOrigin();
  if (u.startsWith('/') && origin) return `${origin}${u}`;
  return u;
}

/** If API only sends DB `image` filename, build URL to Laravel /build/storage/sliders/ */
function imageUrlFromSliderItem(item) {
  let href = item.image_url || item.imageUrl;
  if (href && String(href).trim()) {
    return resolveSliderImageUrl(String(href).trim());
  }
  const name = item.image;
  if (name == null || String(name).trim() === '') return '';
  const basename = String(name).trim().replace(/^\/+/, '').split('/').pop();
  if (!basename) return '';
  const safe = encodeURIComponent(basename);
  const origin = backendOrigin();
  if (origin) return `${origin}/build/storage/sliders/${safe}`;
  return resolveSliderImageUrl(`/build/storage/sliders/${safe}`);
}

export default {
  name: 'HeroBannerOne',
  components: {
    Swiper,
    SwiperSlide,
  },
  data() {
    return {
      slider_data: [],
      swiperModules: [Autoplay, Pagination],
      swiperInstance: null,
      swiperRenderKey: 0,
    };
  },
  computed: {
    displaySliders() {
      return this.slider_data.length ? this.slider_data : FALLBACK_SLIDERS;
    },
    /** Advance slides on a timer (pause on hover / after manual swipe). */
    heroAutoplay() {
      if (this.displaySliders.length < 2) {
        return false;
      }
      return {
        delay: 5500,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      };
    },
  },
  methods: {
    onSwiper(swiper) {
      this.swiperInstance = swiper;
    },
    ctaIsExternal(slider) {
      const u = slider.url && String(slider.url).trim();
      return !!u && /^https?:\/\//i.test(u);
    },
    ctaPath(slider) {
      const u = slider.url && String(slider.url).trim();
      if (!u || this.ctaIsExternal(slider)) return '/shop';
      if (u.startsWith('/')) return u;
      return `/${u}`;
    },
    slideBackgroundStyle(slider) {
      if (slider._gradient) {
        return { backgroundImage: slider._gradient };
      }
      const url = imageUrlFromSliderItem(slider);
      if (url && String(url).trim()) {
        // Quoted url() so spaces / special chars in filenames don’t break CSS
        const escaped = String(url).trim().replace(/\\/g, '\\\\').replace(/'/g, "\\'");
        return { backgroundImage: `url('${escaped}')` };
      }
      return {
        backgroundImage: 'linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)',
      };
    },
    async fetchSliderData() {
      try {
        const response = await this.$axios.get('/get-sliders');
        const list = normalizeSliderPayload(response.data);
        this.slider_data = list.map((item, index) => ({
          ...item,
          id: item.id ?? `slide-${index}`,
          tag: item.tag || 'Shop',
          title: item.title || 'Special offer',
          text: item.text ?? '',
          url: item.url ?? '',
          image_url: imageUrlFromSliderItem(item),
        }));
        this.swiperRenderKey += 1;
      } catch (error) {
        console.error('Failed to fetch slider data:', error);
        this.slider_data = [];
        this.swiperRenderKey += 1;
      }
      await this.$nextTick();
      this.swiperInstance?.update?.();
      this.swiperInstance?.pagination?.render?.();
      this.swiperInstance?.pagination?.update?.();
    },
  },
  mounted() {
    this.fetchSliderData();
  },
};
</script>

<style scoped>
.slider-area {
  position: relative;
}
</style>

<!-- Match legacy .main-slider-paginations look for Swiper’s default pagination -->
<style>
.slider-area .swiper-pagination-horizontal {
  position: absolute;
  bottom: 28px;
  left: 0;
  right: 0;
  text-align: center;
  z-index: 9;
}

@media (max-width: 575px) {
  .slider-area .swiper-pagination-horizontal {
    display: none;
  }
}

.slider-area .swiper-pagination-bullet {
  font-size: 0;
  width: 7px;
  height: 7px;
  background: #fff;
  opacity: 0.35;
  margin: 0 5px;
}

.slider-area .swiper-pagination-bullet-active {
  width: 35px;
  border-radius: 10px;
  background: #fff;
  opacity: 1;
}
</style>